import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Users, MousePointerClick, Target, BarChart3, DollarSign, Copy } from 'lucide-react';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { PartnerLeaderboard } from '../components/affiliates/PartnerLeaderboard';
import useAuthStore from '../lib/auth-store';

export default function AffiliatesPage() {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const loadAffiliates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.request('/affiliates/directory');
      setAffiliates(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAffiliates(); }, [loadAffiliates]);

  return (
    <div className="space-y-6" data-testid="affiliates-page">
      {['super_admin', 'iso'].includes(user?.role) && <PartnerLeaderboard />}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Affiliate Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage affiliates, track clicks, leads, and commissions</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-affiliate-btn">
          <Plus className="h-4 w-4 mr-2" /> New Affiliate
        </Button>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Affiliates" value={affiliates.length} />
        <StatCard icon={MousePointerClick} label="Total Clicks" value={affiliates.reduce((s, a) => s + (a.total_clicks || 0), 0)} />
        <StatCard icon={Target} label="Total Leads" value={affiliates.reduce((s, a) => s + (a.total_leads || 0), 0)} />
        <StatCard icon={DollarSign} label="Total Commissions" value={`$${affiliates.reduce((s, a) => s + (a.total_commissions || 0), 0).toLocaleString()}`} />
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : affiliates.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
          <h3 className="text-lg font-semibold mb-2">No affiliates yet</h3>
          <p className="text-sm text-muted-foreground">Create your first affiliate to start tracking referrals.</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {affiliates.map(aff => (
            <Card key={aff.affiliate_id} className="p-4 cursor-pointer hover:border-primary/30 transition-colors" onClick={() => setSelectedAffiliate(aff)} data-testid={`affiliate-card-${aff.affiliate_id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold">{aff.name}</h3>
                    {aff.company && <span className="text-sm text-muted-foreground">({aff.company})</span>}
                    <Badge variant={aff.status === 'active' ? 'default' : 'secondary'}>{aff.status}</Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                    <span className="font-mono">Code: {aff.affiliate_code}</span>
                    <span>{aff.email}</span>
                    <span>{aff.payout_type?.replace('_', ' ')}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 md:gap-6 text-center md:ml-4">
                  <div><p className="text-lg font-bold">{aff.total_clicks || 0}</p><p className="text-[10px] text-muted-foreground uppercase">Clicks</p></div>
                  <div><p className="text-lg font-bold">{aff.total_leads || 0}</p><p className="text-[10px] text-muted-foreground uppercase">Leads</p></div>
                  <div><p className="text-lg font-bold" data-testid={`affiliate-referrals-${aff.affiliate_id}`}>{aff.referral_count || 0}</p><p className="text-[10px] text-muted-foreground uppercase">Referred</p></div>
                  <Badge variant="outline" className="capitalize hidden sm:inline-flex" data-testid={`affiliate-portal-status-${aff.affiliate_id}`}>
                    {aff.account_status === 'none' ? (aff.invite_eligible ? 'Ready to invite' : `${aff.referral_count || 0}/${aff.invite_threshold || 5} to portal`) : `Portal ${aff.account_status}`}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/affiliates/${aff.affiliate_id}`); }} data-testid={`affiliate-profile-btn-${aff.affiliate_id}`}>Profile</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateAffiliateDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadAffiliates(); }} />
      )}

      {selectedAffiliate && (
        <AffiliateDetailDialog affiliate={selectedAffiliate} open={!!selectedAffiliate} onClose={() => setSelectedAffiliate(null)} onUpdated={() => { setSelectedAffiliate(null); loadAffiliates(); }} />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10"><Icon className="h-5 w-5 text-primary" /></div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
    </Card>
  );
}

function CreateAffiliateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', affiliate_code: '', payout_type: 'per_board', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.createAffiliate(form);
      onCreated();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="create-affiliate-dialog">
        <DialogHeader>
          <DialogTitle>New Affiliate</DialogTitle>
          <DialogDescription>Create an affiliate partner for referral tracking.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="John Doe" data-testid="affiliate-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Company</label>
              <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Acme Corp" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@example.com" />
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="555-0100" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Affiliate Code</label>
              <Input value={form.affiliate_code} onChange={e => setForm({...form, affiliate_code: e.target.value})} placeholder="Auto-generated if empty" />
            </div>
            <div>
              <label className="text-sm font-medium">Payout Type</label>
              <Select value={form.payout_type} onValueChange={v => setForm({...form, payout_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_board">Per Board</SelectItem>
                  <SelectItem value="per_lead">Per Lead</SelectItem>
                  <SelectItem value="revenue_share">Revenue Share</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Notes</label>
            <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!form.name.trim() || saving} data-testid="confirm-create-affiliate">
              {saving ? 'Creating...' : 'Create Affiliate'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AffiliateDetailDialog({ affiliate, open, onClose, onUpdated }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const trackingUrl = `${BACKEND_URL}/forms/YOUR_FORM_SLUG?ref=${affiliate.affiliate_code}`;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="affiliate-detail-dialog">
        <DialogHeader>
          <DialogTitle>{affiliate.name}</DialogTitle>
          <DialogDescription>{affiliate.company || 'Affiliate Partner'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold">{affiliate.total_clicks || 0}</p>
              <p className="text-xs text-muted-foreground">Clicks</p>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold">{affiliate.total_leads || 0}</p>
              <p className="text-xs text-muted-foreground">Leads</p>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-2xl font-bold">{affiliate.total_boarded || 0}</p>
              <p className="text-xs text-muted-foreground">Boarded</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Code</span>
              <span className="font-mono flex items-center gap-1">
                {affiliate.affiliate_code}
                <button onClick={() => navigator.clipboard.writeText(affiliate.affiliate_code)} className="text-muted-foreground hover:text-foreground">
                  <Copy className="h-3 w-3" />
                </button>
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Email</span>
              <span>{affiliate.email || '-'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Phone</span>
              <span>{affiliate.phone || '-'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payout Type</span>
              <span className="capitalize">{affiliate.payout_type?.replace('_', ' ') || '-'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Status</span>
              <Badge variant={affiliate.status === 'active' ? 'default' : 'secondary'}>{affiliate.status}</Badge>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Tracking URL Template</label>
            <div className="flex gap-2 mt-1">
              <Input value={trackingUrl} readOnly className="text-xs font-mono" />
              <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(trackingUrl)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Replace YOUR_FORM_SLUG with your actual form slug.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
